import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { searchProducts, addOrderItem, lookupPricing } from "@/lib/api";
import { Loader2, Plus, Search, Package } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });

export default function AddProductDialog({
  open,
  onOpenChange,
  orderRecordId,
  userRecordId,
  onAdded,
  tier = "",
  skipApiCall = false,
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState("");
  const [msrp, setMsrp] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  // Reset on close
  const close = (next) => {
    if (!next) {
      setQ("");
      setItems([]);
      setPicked(null);
      setQty(1);
      setPrice("");
      setMsrp(null);
    }
    onOpenChange(next);
  };

  // Pick a product and load the customer's role price ("Your price") + MSRP.
  async function pickProduct(p) {
    setPicked(p);
    setMsrp(p.msrp ?? p.price ?? null);
    setPrice(String(p.price ?? ""));
    if (!tier) return;
    setPriceLoading(true);
    try {
      const res = await lookupPricing({ productRecordIds: [p.id], tier });
      const info = (res?.items || [])[0];
      if (info) {
        const onSale = !!info.on_sale && Number(info.sale_price || 0) > 0;
        const rp = onSale ? Number(info.sale_price) : Number(info.role_price);
        if (rp > 0) setPrice(String(rp));
        if (info.msrp != null) setMsrp(Number(info.msrp));
      }
    } catch {
      /* keep base price fallback */
    } finally {
      setPriceLoading(false);
    }
  }

  // Debounced search
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchProducts(q, 30);
        setItems(res.products || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  async function handleAdd() {
    if (!picked) return;
    // Detached mode: parent collects items in local state, no Airtable write.
    if (skipApiCall) {
      onAdded?.({
        product_record_id: picked.id,
        name: picked.name,
        image_url: picked.image_url || "",
        qty: Number(qty) || 1,
        price: price === "" ? Number(picked.price) || 0 : Number(price),
        msrp: msrp != null ? Number(msrp) : Number(picked.msrp ?? picked.price) || 0,
      });
      close(false);
      return;
    }
    setAdding(true);
    try {
      const res = await addOrderItem({
        userRecordId,
        orderRecordId,
        productRecordId: picked.id,
        qty: Number(qty) || 1,
        price: price === "" ? undefined : Number(price),
      });
      toast.success(`Added ${res.item.name}`);
      onAdded?.(res.item);
      close(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not add product");
    } finally {
      setAdding(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="sm:max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto"
        data-testid="add-product-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Plus size={18} className="text-violet-600" />
            Add product to order
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Only active products are shown.
          </DialogDescription>
        </DialogHeader>

        {!picked ? (
          <>
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
              />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products"
                autoFocus
                className="pl-9 h-11 rounded-xl"
                data-testid="add-product-search"
              />
            </div>
            <div className="max-h-80 overflow-y-auto -mx-1 pr-1">
              {loading ? (
                <div className="py-10 text-center text-zinc-500 text-sm">
                  <Loader2 className="animate-spin inline mr-1" size={14} />
                  Searching…
                </div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center text-zinc-500 text-sm">
                  No active products match.
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {items.map((p) => (
                    <li
                      key={p.id}
                      onClick={() => pickProduct(p)}
                      className="cursor-pointer flex items-center gap-3 rounded-xl bg-white border border-zinc-200 hover:border-violet-400 px-3 py-2 transition"
                      data-testid={`add-product-row-${p.id}`}
                    >
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="w-10 h-10 rounded-lg object-cover bg-zinc-100 shrink-0"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0">
                          <Package size={16} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-900 truncate">
                          {p.name}
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          {fmt(p.price)} · inv {p.inventory}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-zinc-200 p-3 flex items-center gap-3">
              {picked.image_url ? (
                <img
                  src={picked.image_url}
                  alt={picked.name}
                  className="w-12 h-12 rounded-lg object-cover bg-zinc-100"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400">
                  <Package size={18} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-900 truncate">
                  {picked.name}
                </div>
                <button
                  onClick={() => setPicked(null)}
                  className="text-xs text-violet-700 font-medium"
                  data-testid="add-product-change"
                >
                  Change product
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="h-11 rounded-xl"
                  data-testid="add-product-qty"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">MSRP</Label>
                <Input
                  type="text"
                  readOnly
                  value={msrp != null ? fmt(msrp) : "—"}
                  className="h-11 rounded-xl bg-zinc-100 text-zinc-500 border-zinc-200"
                  data-testid="add-product-msrp"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1">
                  Your price
                  {priceLoading && <Loader2 size={11} className="animate-spin text-violet-500" />}
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="h-11 rounded-xl border-violet-300 font-semibold"
                  data-testid="add-product-price"
                />
              </div>
            </div>
            <div className="text-sm text-zinc-700 text-right">
              Line total:{" "}
              <span className="font-semibold text-zinc-900">
                {fmt((Number(qty) || 0) * (Number(price) || 0))}
              </span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 pt-2">
          <Button variant="ghost" onClick={() => close(false)} className="rounded-xl">
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!picked || adding}
            className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="add-product-confirm"
          >
            {adding ? (
              <Loader2 className="animate-spin mr-2" size={14} />
            ) : (
              <Plus size={14} className="mr-2" />
            )}
            Add to order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
